import type { TenantMarketingProvider } from '../types';
import {
  type GoogleAdsPublicConfig,
  type GoogleAnalytics4PublicConfig,
  type GoogleTagManagerPublicConfig,
  parseGoogleAdsPublicConfig,
  parseGoogleAnalytics4PublicConfig,
  parseGoogleTagManagerPublicConfig,
} from './config';

export interface TenantGoogleAnalyticsIntegrationCandidate {
  readonly provider: TenantMarketingProvider;
  readonly enabled: boolean;
  readonly publicConfig: unknown;
}

export type TenantGoogleAnalyticsIntegrationSelection =
  | {
      readonly provider: 'google_tag_manager';
      readonly publicConfig: GoogleTagManagerPublicConfig;
    }
  | {
      readonly provider: 'google_direct';
      readonly googleAnalytics4: GoogleAnalytics4PublicConfig | null;
      readonly googleAds: GoogleAdsPublicConfig | null;
    };

/**
 * Selects one Google measurement transport for a tenant page.
 *
 * GTM owns all Google tags when both integrations are enabled. This prevents a
 * direct GA4 tag and a GA4 tag inside GTM from recording the same event twice.
 */
export function selectTenantGoogleAnalyticsIntegration(
  integrations: readonly TenantGoogleAnalyticsIntegrationCandidate[],
): TenantGoogleAnalyticsIntegrationSelection | null {
  for (const integration of integrations) {
    if (
      integration.enabled === true &&
      integration.provider === 'google_tag_manager'
    ) {
      const publicConfig = parseGoogleTagManagerPublicConfig(
        integration.publicConfig,
      );

      if (publicConfig) {
        return Object.freeze({
          provider: 'google_tag_manager',
          publicConfig,
        });
      }
    }
  }

  let googleAnalytics4: GoogleAnalytics4PublicConfig | null = null;
  let googleAds: GoogleAdsPublicConfig | null = null;

  for (const integration of integrations) {
    if (
      integration.enabled === true &&
      integration.provider === 'google_analytics_4'
    ) {
      const publicConfig = parseGoogleAnalytics4PublicConfig(
        integration.publicConfig,
      );

      if (publicConfig) {
        googleAnalytics4 = publicConfig;
      }
    } else if (
      integration.enabled === true &&
      integration.provider === 'google_ads'
    ) {
      googleAds = parseGoogleAdsPublicConfig(integration.publicConfig);
    }
  }

  if (!googleAnalytics4 && !googleAds) {
    return null;
  }

  return Object.freeze({
    provider: 'google_direct' as const,
    googleAnalytics4,
    googleAds,
  });
}
